import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X, ArrowRight, ArrowLeft, CheckCircle2, AlertTriangle, Repeat,
  ShieldCheck, RefreshCw, Eye
} from 'lucide-react';
import { creditosAPI, productosFinancierosAPI, describirError } from '../../services/api';
import { formatoMoneda } from '../../utils/finanzas';
import '../../styles/Renovacion.css';
import { etiquetaProducto, etiquetaFrecuencia, NO_DISPONIBLE } from '../../utils/historicos';

const PASOS = [
  { num: 1, titulo: 'Crédito Actual' },
  { num: 2, titulo: 'Condiciones Actuales' },
  { num: 3, titulo: 'Nueva Propuesta' },
  { num: 4, titulo: 'Comparativo' },
  { num: 5, titulo: 'Confirmación' }
];

export default function RenovacionModal({ credito, onClose, onRenovacionExitosa }) {
  const navigate = useNavigate();
  const [paso, setPaso] = useState(1);
  const [loading, setLoading] = useState(false);
  const [productos, setProductos] = useState([]);
  const [elegibilidad, setElegibilidad] = useState(null);
  const [errorNegocio, setErrorNegocio] = useState(null);
  const [errorTecnico, setErrorTecnico] = useState(null);

  // Formulario nueva propuesta
  const [productoVersionId, setProductoVersionId] = useState('');
  const [montoSolicitado, setMontoSolicitado] = useState(Number(credito?.montoSolicitado || 10000));
  const [fechaDesembolso, setFechaDesembolso] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [tipoGarantia, setTipoGarantia] = useState(credito?.tipoGarantia || '');
  const [garantiaDescripcion, setGarantiaDescripcion] = useState(credito?.garantiaDescripcion || '');
  const [valorGarantia, setValorGarantia] = useState(credito?.valorGarantia || '');
  const [observaciones, setObservaciones] = useState('');

  // Previsualización en vivo de la propuesta
  const [calculandoPreview, setCalculandoPreview] = useState(false);
  const [previewPropuesta, setPreviewPropuesta] = useState(null);

  // Estado de procesamiento y éxito
  const [procesando, setProcesando] = useState(false);
  const [resultadoExitoso, setResultadoExitoso] = useState(null);

  const moneda = formatoMoneda;

  // Cargar elegibilidad y catálogo de productos al montar
  useEffect(() => {
    let activo = true;

    async function cargarInicial() {
      setLoading(true);
      setErrorNegocio(null);
      setErrorTecnico(null);
      try {
        const [elegRes, prodRes] = await Promise.all([
          creditosAPI.obtenerElegibilidad(credito.id),
          productosFinancierosAPI.listar({ activo: true })
        ]);

        if (!activo) return;

        setElegibilidad(elegRes.data?.data || elegRes.data);

        const prods = (prodRes.data?.data || prodRes.data || []).filter(
          (p) => p.activo && p.visibleOriginacion && p.versionVigente
        );
        setProductos(prods);

        // Preseleccionar la version VIGENTE del MISMO producto del credito. Solo se ofrece la vigente de cada producto (el
        // backend rechaza cualquier otra con VERSION_NO_VIGENTE); si el producto ya no es originable no se elige otro en
        // silencio: queda sin seleccionar y la persona debe escoger.
        const codigoActual = credito.producto?.codigo;
        const delMismoProducto = prods.find((p) => p.codigo === codigoActual)?.versionVigente;
        if (credito.productoReferencial || !delMismoProducto) {
          // Credito historico (su producto es un marcador) o producto ya no disponible: el usuario debe elegir.
          setProductoVersionId('');
        } else {
          setProductoVersionId(delMismoProducto.id);
        }
      } catch (err) {
        if (!activo) return;
        const desc = describirError(err);
        setErrorTecnico(desc.mensaje);
      } finally {
        if (activo) setLoading(false);
      }
    }

    if (credito?.id) {
      cargarInicial();
    }

    return () => {
      activo = false;
    };
  }, [credito?.id, credito?.productoVersionId, credito?.producto?.codigo, credito?.productoReferencial]);

  // Recalcular previsualización cuando cambian condiciones del paso 3
  const calcularPrevisualizacion = useCallback(async () => {
    if (!productoVersionId || !montoSolicitado || Number(montoSolicitado) <= 0 || !fechaDesembolso) {
      return;
    }

    setCalculandoPreview(true);
    try {
      const res = await creditosAPI.previsualizarBorrador({
        productoVersionId,
        montoSolicitado: Number(montoSolicitado),
        fechaDesembolso
      });
      setPreviewPropuesta(res.data);
      setErrorNegocio(null);
    } catch (e) {
      const desc = describirError(e);
      setErrorNegocio(desc.mensaje);
    } finally {
      setCalculandoPreview(false);
    }
  }, [productoVersionId, montoSolicitado, fechaDesembolso]);

  useEffect(() => {
    if (paso === 3 || paso === 4) {
      calcularPrevisualizacion();
    }
  }, [paso, calcularPrevisualizacion]);

  // Ejecutar confirmación de la renovación
  const handleConfirmarRenovacion = async () => {
    if (procesando || resultadoExitoso) return;

    setProcesando(true);
    setErrorNegocio(null);
    setErrorTecnico(null);

    try {
      const payload = {
        productoVersionId,
        montoSolicitado: Number(montoSolicitado),
        fechaDesembolso,
        tipoGarantia: tipoGarantia.trim() || undefined,
        garantiaDescripcion: garantiaDescripcion.trim() || undefined,
        valorGarantia: valorGarantia ? Number(valorGarantia) : undefined,
        observaciones: observaciones.trim() || `Renovación de crédito ${credito.folio}`
      };

      const res = await creditosAPI.renovar(credito.id, payload);
      const nuevoCredito = res.data;

      setResultadoExitoso(nuevoCredito);
      if (onRenovacionExitosa) {
        onRenovacionExitosa(nuevoCredito);
      }
    } catch (e) {
      const desc = describirError(e);
      if (e.response?.status === 409 || e.response?.status === 422) {
        setErrorNegocio(desc.mensaje);
      } else {
        setErrorTecnico(desc.mensaje);
      }
    } finally {
      setProcesando(false);
    }
  };

  const tipoReal = credito.tipoCreditoCanonico || (credito.tipoCredito === 'PERSONAL' ? 'INDIVIDUAL' : credito.tipoCredito);
  const grupoColocadora = credito.grupoNombre || credito.colocadoraNombre || (credito.agrupacion ? credito.agrupacion.nombre : null);
  const saldoActual = Number(credito.saldoActual ?? 0);
  const saldoMoratorios = Number(credito.saldoMoratorios ?? credito.mora ?? 0);
  // Los remanentes de pagos parciales del sistema anterior no son cuotas del calendario contractual
  const cuotasContractuales = (credito.cuotas || []).filter((q) => !q.esRemanente);
  const totalCuotas = cuotasContractuales.length || credito.numeroPagosSnapshot || 0;
  // Una cuota con cierre parcial (resto en remanente) NO cuenta como pagada
  const cuotasPagadas = cuotasContractuales.filter((q) => !q.trasladadoARemanente && (q.estadoCuota === 'PAGADA' || (Number(q.saldoCuota) === 0 && Number(q.importePagado) > 0))).length;

  return (
    <div className="renovacion-modal-overlay" onClick={onClose}>
      <div className="renovacion-modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Cabecera del Stepper */}
        <header className="renovacion-modal-header">
          <div className="renovacion-header-title">
            <span className="renovacion-icon-badge">
              <Repeat size={18} />
            </span>
            <div>
              <h3>Workflow de Renovación de Crédito</h3>
              <p>Crédito Origen: <strong>{credito.folio}</strong> · {credito.titular?.nombreCompleto || credito.nombreGrupo}</p>
            </div>
          </div>
          <button type="button" className="btn btn-ghost btn-icon" onClick={onClose} aria-label="Cerrar modal">
            <X size={20} />
          </button>
        </header>

        {/* Barra de progreso de pasos */}
        <nav className="renovacion-stepper-nav">
          {PASOS.map((p) => {
            const isDone = paso > p.num;
            const isActual = paso === p.num;
            return (
              <div
                key={p.num}
                className={`renovacion-step-item ${isActual ? 'is-actual' : ''} ${isDone ? 'is-done' : ''}`}
                onClick={() => {
                  if (resultadoExitoso) return;
                  if (p.num < paso) setPaso(p.num);
                }}
              >
                <div className="step-circle">
                  {isDone ? <CheckCircle2 size={16} /> : p.num}
                </div>
                <span className="step-label">{p.titulo}</span>
              </div>
            );
          })}
        </nav>

        {/* Mensajes de error */}
        {errorNegocio && (
          <div className="renovacion-alert is-warning">
            <AlertTriangle size={18} />
            <div>
              <strong>Validación de Negocio:</strong>
              <p>{errorNegocio}</p>
            </div>
          </div>
        )}

        {errorTecnico && (
          <div className="renovacion-alert is-error">
            <AlertTriangle size={18} />
            <div>
              <strong>Error Técnico:</strong>
              <p>{errorTecnico}</p>
            </div>
          </div>
        )}

        {/* Contenido dinámico según el paso */}
        <div className="renovacion-modal-body">
          {loading ? (
            <div className="renovacion-loading">
              <RefreshCw className="spin" size={24} />
              <p>Verificando elegibilidad y cargando condiciones...</p>
            </div>
          ) : (
            <>
              {/* ── PASO 1: Crédito Actual ── */}
              {paso === 1 && (
                <div className="renovacion-step-panel">
                  <h4>Paso 1: Información General del Crédito Origen</h4>
                  <div className="renovacion-grid-2">
                    <div className="renovacion-card">
                      <h5>Datos del Crédito</h5>
                      <dl className="renovacion-dl">
                        <div><dt>Folio</dt><dd className="font-mono font-bold">{credito.folio}</dd></div>
                        <div><dt>Modalidad / Tipo</dt><dd><span className={`entity-badge entity-badge--${tipoReal.toLowerCase()}`}>{tipoReal}</span></dd></div>
                        {grupoColocadora && (
                          <div><dt>{tipoReal === 'GRUPAL' ? 'Grupo' : 'Colocadora'}</dt><dd className="font-semibold">{grupoColocadora}</dd></div>
                        )}
                        <div><dt>Producto Actual</dt><dd>{etiquetaProducto(credito)}</dd></div>
                        <div><dt>Monto Original</dt><dd className="font-bold">{moneda(credito.montoSolicitado)}</dd></div>
                        <div><dt>Estado Actual</dt><dd><span className="status-tag">{credito.estado}</span></dd></div>
                      </dl>
                    </div>

                    <div className="renovacion-card">
                      <h5>Situación Financiera y Saldo</h5>
                      <dl className="renovacion-dl">
                        <div><dt>Saldo Actual Insoluto</dt><dd className="font-bold text-success">{moneda(saldoActual)}</dd></div>
                        <div><dt>Mora Devengada</dt><dd className={saldoMoratorios > 0 ? 'text-peligro font-bold' : ''}>{moneda(saldoMoratorios)}</dd></div>
                        <div><dt>Cuotas Pagadas</dt><dd>{cuotasPagadas} de {totalCuotas} cuotas</dd></div>
                        <div><dt>Fecha Desembolso</dt><dd>{credito.fechaDesembolso ? new Date(credito.fechaDesembolso).toLocaleDateString('es-MX') : '—'}</dd></div>
                      </dl>
                    </div>
                  </div>

                  {/* Evaluación de Elegibilidad */}
                  {elegibilidad && (
                    <div className="renovacion-card" style={{ marginTop: '1rem' }}>
                      <h5>Matriz de Elegibilidad de Renovación</h5>
                      <div className="elegibilidad-list">
                        {elegibilidad.criterios?.map((crit) => (
                          <div key={crit.criterio} className={`elegibilidad-item is-${crit.estado.toLowerCase()}`}>
                            <span className="elegibilidad-badge">{crit.estado}</span>
                            <div>
                              <strong>{crit.titulo}</strong>
                              <p>{crit.detalle}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                      {!elegibilidad.esElegible && (
                        <p className="text-peligro" style={{ marginTop: '0.5rem', fontWeight: 600 }}>
                          * El crédito presenta bloqueos obligatorios que impiden continuar con la renovación.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* ── PASO 2: Condiciones Actuales ── */}
              {paso === 2 && (
                <div className="renovacion-step-panel">
                  <h4>Paso 2: Condiciones y Comportamiento Financiero</h4>
                  <div className="renovacion-grid-3">
                    <div className="renovacion-kpi">
                      <span className="kpi-title">Plazo y Frecuencia</span>
                      <span className="kpi-value">{credito.productoReferencial ? NO_DISPONIBLE : `${credito.numeroPagosSnapshot || totalCuotas} pagos`}</span>
                      <span className="kpi-sub">{credito.productoReferencial ? `Frecuencia: ${etiquetaFrecuencia(credito)}` : (credito.tipoProgramacionSnapshot || 'SEMANAL / INTERVALO')}</span>
                    </div>
                    <div className="renovacion-kpi">
                      <span className="kpi-title">Pago Periódico Regular</span>
                      <span className="kpi-value text-teal">{moneda(credito.pagoRegularSnapshot || 0)}</span>
                      <span className="kpi-sub">Total: {moneda(credito.totalPagarSnapshot || 0)}</span>
                    </div>
                    <div className="renovacion-kpi">
                      <span className="kpi-title">Tasa / Interés Pactado</span>
                      <span className="kpi-value">{credito.interesPorcentajeSnapshot ? `${credito.interesPorcentajeSnapshot}%` : '—'}</span>
                      <span className="kpi-sub">Total interés: {moneda(credito.interesTotalSnapshot || 0)}</span>
                    </div>
                  </div>

                  <div className="renovacion-card" style={{ marginTop: '1rem' }}>
                    <h5>Resumen de Garantía Registrada</h5>
                    <dl className="renovacion-dl">
                      <div><dt>Tipo Garantía</dt><dd>{credito.tipoGarantia || 'Sin tipo registrado'}</dd></div>
                      <div><dt>Descripción</dt><dd>{credito.garantiaDescripcion || 'Sin descripción'}</dd></div>
                      <div><dt>Valor Declarado</dt><dd>{credito.valorGarantia ? moneda(credito.valorGarantia) : '—'}</dd></div>
                    </dl>
                  </div>
                </div>
              )}

              {/* ── PASO 3: Nueva Propuesta ── */}
              {paso === 3 && (
                <div className="renovacion-step-panel">
                  <h4>Paso 3: Configurar Nueva Propuesta de Renovación</h4>
                  <div className="renovacion-form-grid">
                    <div className="form-group">
                      <label>Producto Financiero / Versión Vigente</label>
                      <select
                        value={productoVersionId}
                        onChange={(e) => setProductoVersionId(e.target.value)}
                        className="form-control"
                        data-testid="renovacion-producto"
                      >
                        {(credito.productoReferencial || !productoVersionId) && (
                          <option value="">— Seleccione el producto (obligatorio) —</option>
                        )}
                        {productos.map((prod) =>
                          [prod.versionVigente].map((ver) => (
                            <option key={ver.id} value={ver.id}>
                              {prod.nombre} · {ver.numeroPagos} pagos ({ver.tipoProgramacionPago}) - Versión {ver.version}
                            </option>
                          ))
                        )}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Nuevo Monto Solicitado ($ MXN)</label>
                      <input
                        type="number"
                        min="1000"
                        step="500"
                        value={montoSolicitado}
                        onChange={(e) => setMontoSolicitado(e.target.value)}
                        className="form-control font-mono font-bold"
                      />
                    </div>

                    <div className="form-group">
                      <label>Fecha de Desembolso Estimada</label>
                      <input
                        type="date"
                        value={fechaDesembolso}
                        onChange={(e) => setFechaDesembolso(e.target.value)}
                        className="form-control"
                      />
                    </div>

                    <div className="form-group">
                      <label>Tipo de Garantía (Opcional)</label>
                      <input
                        type="text"
                        placeholder="Ej. PRENDARIA / AVAL"
                        value={tipoGarantia}
                        onChange={(e) => setTipoGarantia(e.target.value)}
                        className="form-control"
                      />
                    </div>

                    <div className="form-group">
                      <label>Valor Estimado de Garantía ($)</label>
                      <input
                        type="number"
                        min="0"
                        step="500"
                        placeholder="0.00"
                        value={valorGarantia}
                        onChange={(e) => setValorGarantia(e.target.value)}
                        className="form-control font-mono"
                      />
                    </div>

                    <div className="form-group full-width">
                      <label>Descripción del Bien o Garantía</label>
                      <input
                        type="text"
                        placeholder="Descripción de la prenda o activo en garantía"
                        value={garantiaDescripcion}
                        onChange={(e) => setGarantiaDescripcion(e.target.value)}
                        className="form-control"
                      />
                    </div>

                    <div className="form-group full-width">
                      <label>Observaciones de Renovación</label>
                      <textarea
                        rows={2}
                        placeholder={`Motivo de la renovación del crédito ${credito.folio}`}
                        value={observaciones}
                        onChange={(e) => setObservaciones(e.target.value)}
                        className="form-control"
                      />
                    </div>
                  </div>

                  {/* Previsualización rápida calculada */}
                  {previewPropuesta && (
                    <div className="renovacion-preview-card" style={{ marginTop: '1rem' }}>
                      <div className="preview-header">
                        <h5>Cálculo Financiero Preliminar</h5>
                        {calculandoPreview && <RefreshCw className="spin" size={14} />}
                      </div>
                      <div className="renovacion-grid-3">
                        <div>
                          <span className="cell-muted">Pago Periódico:</span>
                          <strong className="text-teal" style={{ display: 'block', fontSize: '1.125rem' }}>
                            {moneda(previewPropuesta.pagoRegular)}
                          </strong>
                        </div>
                        <div>
                          <span className="cell-muted">Total a Pagar:</span>
                          <strong style={{ display: 'block', fontSize: '1.125rem' }}>
                            {moneda(previewPropuesta.totalPagar)}
                          </strong>
                        </div>
                        <div>
                          <span className="cell-muted">Primer Vencimiento:</span>
                          <strong style={{ display: 'block' }}>
                            {previewPropuesta.primerVencimiento ? new Date(previewPropuesta.primerVencimiento).toLocaleDateString('es-MX') : '—'}
                          </strong>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── PASO 4: Resumen Comparativo ── */}
              {paso === 4 && (
                <div className="renovacion-step-panel">
                  <h4>Paso 4: Comparativo Crédito Actual vs Nueva Renovación</h4>
                  <div className="renovacion-comparativo-table-wrap">
                    <table className="renovacion-table">
                      <thead>
                        <tr>
                          <th>Concepto Financiero</th>
                          <th>Crédito Actual ({credito.folio})</th>
                          <th className="th-destacado">Nueva Renovación (Propuesta)</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><strong>Monto Financiado</strong></td>
                          <td>{moneda(credito.montoSolicitado)}</td>
                          <td className="td-destacado font-bold text-teal">{moneda(montoSolicitado)}</td>
                        </tr>
                        <tr>
                          <td><strong>Plazo / Cuotas</strong></td>
                          <td>{credito.numeroPagosSnapshot || totalCuotas} pagos</td>
                          <td className="td-destacado font-bold">{previewPropuesta?.numeroPagos || '—'} pagos</td>
                        </tr>
                        <tr>
                          <td><strong>Pago Periódico</strong></td>
                          <td>{moneda(credito.pagoRegularSnapshot || 0)}</td>
                          <td className="td-destacado font-bold text-success">{previewPropuesta ? moneda(previewPropuesta.pagoRegular) : '—'}</td>
                        </tr>
                        <tr>
                          <td><strong>Total Ordinario Programado</strong></td>
                          <td>{moneda(credito.totalPagarSnapshot || 0)}</td>
                          <td className="td-destacado font-bold">{previewPropuesta ? moneda(previewPropuesta.totalPagar) : '—'}</td>
                        </tr>
                        <tr>
                          <td><strong>Interés Ordinario Total</strong></td>
                          <td>{moneda(credito.interesTotalSnapshot || 0)}</td>
                          <td className="td-destacado">{previewPropuesta ? moneda(previewPropuesta.interesTotal) : '—'}</td>
                        </tr>
                        <tr>
                          <td><strong>Primer Vencimiento</strong></td>
                          <td>{credito.primerVencimientoSnapshot ? new Date(credito.primerVencimientoSnapshot).toLocaleDateString('es-MX') : '—'}</td>
                          <td className="td-destacado">{previewPropuesta?.primerVencimiento ? new Date(previewPropuesta.primerVencimiento).toLocaleDateString('es-MX') : '—'}</td>
                        </tr>
                        <tr>
                          <td><strong>Modalidad Preservada</strong></td>
                          <td>{tipoReal} {grupoColocadora ? `(${grupoColocadora})` : ''}</td>
                          <td className="td-destacado">{tipoReal} {grupoColocadora ? `(${grupoColocadora})` : ''}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* ── PASO 5: Confirmación y Envío / Resultado ── */}
              {paso === 5 && (
                <div className="renovacion-step-panel">
                  {resultadoExitoso ? (
                    <div className="renovacion-exito-box">
                      <CheckCircle2 size={48} className="text-success" />
                      <h3>¡RENOVACIÓN CREADA CON ÉXITO!</h3>
                      <p className="exito-sub">
                        Se ha generado el nuevo crédito <strong>{resultadoExitoso.folio}</strong> vinculado al crédito anterior <strong>{credito.folio}</strong>.
                      </p>

                      <div className="exito-card">
                        <dl className="renovacion-dl">
                          <div><dt>Nuevo Folio</dt><dd className="font-mono font-bold text-teal">{resultadoExitoso.folio}</dd></div>
                          <div><dt>Crédito Anterior</dt><dd className="font-mono">{credito.folio}</dd></div>
                          <div><dt>Cliente Titular</dt><dd>{credito.titular?.nombreCompleto || credito.nombreGrupo}</dd></div>
                          <div><dt>Monto Aprobado</dt><dd className="font-bold">{moneda(resultadoExitoso.montoSolicitado)}</dd></div>
                          <div><dt>Estado Inicial</dt><dd><span className="status-tag">{resultadoExitoso.estado}</span></dd></div>
                        </dl>
                      </div>

                      <div className="exito-acciones">
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={() => {
                            onClose();
                            navigate(`/creditos/${resultadoExitoso.id}`);
                          }}
                        >
                          <Eye size={16} />
                          VER NUEVO CRÉDITO ({resultadoExitoso.folio})
                        </button>
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                          Cerrar
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="renovacion-confirm-box">
                      <div className="confirm-icon">
                        <ShieldCheck size={40} className="text-teal" />
                      </div>
                      <h4>Confirmación de Formalización de Renovación</h4>
                      <p>
                        Está a punto de crear una renovación formal para el crédito <strong>{credito.folio}</strong>.
                        El sistema generará un nuevo crédito en estado <strong>BORRADOR</strong> con trazabilidad inmutable bidireccional y paquete documental On-Demand listo para autorizar.
                      </p>

                      <div className="renovacion-card" style={{ textAlign: 'left', margin: '1.5rem 0' }}>
                        <dl className="renovacion-dl">
                          <div><dt>Titular</dt><dd>{credito.titular?.nombreCompleto || credito.nombreGrupo}</dd></div>
                          <div><dt>Monto Nuevo Solicitado</dt><dd className="font-bold text-teal">{moneda(montoSolicitado)}</dd></div>
                          <div><dt>Cuota Periódica Estimada</dt><dd className="font-bold">{previewPropuesta ? moneda(previewPropuesta.pagoRegular) : '—'}</dd></div>
                          <div><dt>Fecha Desembolso</dt><dd>{fechaDesembolso}</dd></div>
                        </dl>
                      </div>

                      <div className="confirm-buttons">
                        <button
                          type="button"
                          className="btn btn-success btn-lg"
                          disabled={procesando}
                          onClick={handleConfirmarRenovacion}
                        >
                          {procesando ? (
                            <>
                              <RefreshCw className="spin" size={18} />
                              Procesando Renovación Transaccional…
                            </>
                          ) : (
                            <>
                              <CheckCircle2 size={18} />
                              CONFIRMAR Y GENERAR RENOVACIÓN
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer con botones de navegación */}
        {!resultadoExitoso && (
          <footer className="renovacion-modal-footer">
            <div>
              {paso > 1 && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={procesando}
                  onClick={() => setPaso((prev) => Math.max(1, prev - 1))}
                >
                  <ArrowLeft size={16} />
                  Anterior
                </button>
              )}
            </div>
            <div>
              {paso < 5 ? (
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={loading || (paso === 1 && elegibilidad && !elegibilidad.esElegible) || (paso === 3 && !productoVersionId)}
                  onClick={() => setPaso((prev) => Math.min(5, prev + 1))}
                >
                  Siguiente
                  <ArrowRight size={16} />
                </button>
              ) : null}
            </div>
          </footer>
        )}
      </div>
    </div>
  );
}
